import clsx from 'clsx';
import React, { DOMAttributes, ForwardedRef } from 'react';
import styles from '@danske/sapphire-css/components/modalLayout/modalLayout.module.css';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useScrollCheck,
} from '../../utils';
import { useThemeCheck } from '../../utils/useThemeCheck';
import { DOMProps } from '@react-types/shared';
import { useObjectRef } from '@react-aria/utils';
import { GlobalDomAttributes } from '../../utils/types';

export interface ModalLayoutBodyProps
  extends DOMProps,
    GlobalDomAttributes,
    DOMAttributes<HTMLDivElement>,
    Pick<SapphireStyleProps, 'UNSAFE_style' | 'UNSAFE_className'> {
  children: React.ReactNode;
  noPadding?: boolean;
}

/**
 * Used in {@link ModalLayoutProps#body body} part of {@link ModalLayout}.
 * - Ensures consistent padding with header and footer.
 * - Renders a top divider when the content is scrolled.
 */
export const ModalLayoutBody = React.forwardRef(function ModalLayoutBody(
  { children, noPadding, ...rest }: ModalLayoutBodyProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
): JSX.Element {
  useThemeCheck();

  const bodyRef = useObjectRef(forwardedRef);
  const { isScrollable, isScrolled, scrollCheckProps } =
    useScrollCheck(bodyRef);

  const { styleProps, filteredProps } = useSapphireStyleProps(rest);

  return (
    <div
      ref={bodyRef}
      {...scrollCheckProps}
      {...filteredProps}
      style={styleProps.style}
      className={clsx(
        styleProps.className,
        styles['sapphire-modal-layout__body'],
        {
          [styles['sapphire-modal-layout__body--no-padding']]: noPadding,
          [styles['sapphire-modal-layout__body--scrollable']]: isScrollable,
          [styles['sapphire-modal-layout__body--scrolled']]: isScrolled,
        }
      )}
    >
      {children}
    </div>
  );
});
